import React, { useEffect, useRef, useState } from "react";
import styles from "./HomePage.module.css";
import HomePageImage from "../../assets/default-article.jpg";
import GraphIcon from "../../assets/ft_3.svg";
import roadMap from "../../assets/ft_1.svg";
import userIcon from "../../assets/ft_4.svg";
import axios from "axios";
import { apiEndpoint, availabilityMap } from "../../DummyData/DUMMYDATA";
import { useTranslation } from "../../I18n/Languages/LanguageContext";
import CustomSelecter from "../../components/Input/Autocomplete/Autocomplete";
import ImageCarousel from "../../components/Testimonials/ImageCarousel";
import FAQsComponent from "../../components/FAQs/FAQs";
import JobListings from "../../components/JobListing/JobListing";
import Footer from "../../components/Footer/Footer";
import StaffImage from "../../assets/staff.png";
import Reason5 from "../../assets/ft_5.svg";
import CircularIndeterminate from "../../components/Spinner/Spinner";
import Navbar from "../../components/Navbar/Navbar";

const reasonIcons = [roadMap, GraphIcon, userIcon, Reason5];

const HomePage = () => {
  const [jobsPositions, setJobsPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useTranslation();
  const jobListingsRef = useRef(null);

  const fetchJobListings = async () => {
    setLoading(true);
    try {
      const [externalRes, bothRes] = await Promise.all([
        axios.get(`${apiEndpoint}/job-listings/type/external`),
        axios.get(`${apiEndpoint}/job-listings/type/both`),
      ]);
      const combined = [
        ...(externalRes.data.data || []),
        ...(bothRes.data.data || []),
      ];
      const seen = new Set();
      const deduplicated = combined.filter((job) => {
        if (job.publicationType === "internal") return false;
        if (seen.has(job.id)) return false;
        seen.add(job.id);
        return true;
      });
      setJobsPositions(deduplicated);
    } catch (error) {
      console.error(t("error_fetching_jobs"), error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobListings();
  }, []);

  const jobList = jobsPositions.map((job) => job.jobTitle);

  const handleExploreClick = () => {
    jobListingsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (
      window.location.hash === "#jobs" &&
      sessionStorage.getItem("scrollToJobs") === "true"
    ) {
      setTimeout(() => {
        jobListingsRef.current?.scrollIntoView({ behavior: "smooth" });
        sessionStorage.removeItem("scrollToJobs");
      }, 300);
    }
  }, []);

  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedAvailability, setSelectedAvailability] = useState("");

  const filteredJobs = jobsPositions.filter((job) => {
    const depMatch =
      !selectedDepartment ||
      selectedDepartment === "" ||
      job.workingPositionDepartment?.name === selectedDepartment;

    const locMatch =
      !selectedLocation ||
      selectedLocation === "" ||
      job.location === selectedLocation;

    const availabilityMatch =
      !selectedAvailability ||
      selectedAvailability === "" ||
      availabilityMap[job.availabilityToWork] === selectedAvailability;

    return depMatch && locMatch && availabilityMatch;
  });

  const optionsDepartment = [
    { value: "", label: t("All") },
    ...Array.from(
      new Set(
        jobsPositions
          .map((job) => job.workingPositionDepartment?.name)
          .filter(Boolean)
      )
    ).map((dep) => ({ value: dep, label: dep })),
  ];

  const optionsLocation = [
    { value: "", label: t("All") },
    ...Array.from(
      new Set(jobsPositions.map((job) => job.location).filter(Boolean))
    ).map((loc) => ({ value: loc, label: loc })),
  ];

  const optionsAvailability = [
    { value: "", label: t("All") },
    ...Array.from(
      new Set(
        jobsPositions.map((job) => job.availabilityToWork).filter(Boolean)
      )
    ).map((avail) => ({
      value: availabilityMap[avail] || avail,
      label: availabilityMap[avail] || avail,
    })),
  ];

  const EmptyArrayMessage = () => {
    return (
      <div className={styles.emptyMessages}>{t("no_positions_available")}</div>
    );
  };

  return (
    <div className={styles.container}>
      <Navbar />
      <div
        className={styles.heroSection}
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.6) 100%), url(${HomePageImage})`,
        }}
      >
        <div className={styles.heroOverlay}>
          <h1 className={styles.heroTitle}>{t("Join")}</h1>
          <h3 className={styles.firstTitle}>{t("work_place")}</h3>
          <button
            className={styles.exploreBtnBottom}
            onClick={handleExploreClick}
          >
            {t("explorePositions")}
          </button>
        </div>
      </div>
      <div className={styles.contentSection}>
        <div className={styles.contentContainer}>
          <div className={styles.aboutContanier}>
            <img className={styles.img} src={StaffImage} alt="files" />
            <div className={styles.grid}>
              <div className={styles.mainTitle}>{t("home_title")}</div>
              {[1, 2, 3, 4].map((i, index) => (
                <div key={i} className={styles.flex}>
                  <img src={reasonIcons[index]} alt={`reason${i}_icon`} />
                  <div className={styles.span}>
                    {t(`title${i}`)}
                    {/* <span className={styles.reasons1}>{t(`reason${i}`)}</span> */}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.howtoApplayContanier}>
            <div className={styles.howToApply}>
              <div className={styles.mainTitle}>{t("how_to_apply")}</div>
              <div className={styles.stepsList}>
                {t("how_to_apply_steps", { returnObjects: true }).map(
                  (step, index) => (
                    <div key={index} className={styles.stepItem}>
                      <span className={styles.stepNumber}>{index + 1}.</span>
                      <span className={styles.stepText}>
                        {typeof step === "string" ? (
                          step
                        ) : (
                          <>
                            <strong>{step.bold}</strong>{" "}
                            <span className={styles.normalText}>
                              {step.normal}
                            </span>
                          </>
                        )}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
          <div className={styles.divider} />

          <div className={styles.mainTitle}>{t("open_positions")}</div>
          <div style={{ marginTop: "25px" }}></div>
          <div className={styles.inputs}>
            <CustomSelecter
              value={selectedDepartment}
              options={optionsDepartment}
              topLabel={t("Department")}
              name="department"
              onChange={(value) => setSelectedDepartment(value)}
            />

            <CustomSelecter
              value={selectedAvailability}
              options={optionsAvailability}
              topLabel={t("Availability")}
              name="availability"
              onChange={(value) => setSelectedAvailability(value)}
            />

            <CustomSelecter
              value={selectedLocation}
              options={optionsLocation}
              topLabel={t("Location")}
              name="location"
              onChange={(value) => setSelectedLocation(value)}
            />
          </div>
          {loading ? (
            <div className={styles.loadingContainer}>
              <CircularIndeterminate size={50} />
            </div>
          ) : jobsPositions.length === 0 ? (
            <EmptyArrayMessage />
          ) : (
            <div ref={jobListingsRef} id="job-listings">
              <JobListings
                jobsPositions={filteredJobs}
                jobList={jobList}
                language={language}
                t={t}
                availabilityMap={availabilityMap}
              />
            </div>
          )}
          <div className={styles.divider} />
          <div className={styles.secneSection}>
            <div className={styles.senceCarousel}>
              <ImageCarousel />
            </div>
          </div>
          <div className={styles.divider} />
          <FAQsComponent />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HomePage;
